/**
 * Region gating for the Singapore product (this SPA + the FastAPI backend).
 *
 * OPTRA Singapore (/sg) and OPTRA US (/us/app) share one Clerk instance. Which product an
 * account may use lives in Clerk user public metadata:
 *
 *     { "regions": ["SG"] }          Singapore only
 *     { "regions": ["US"] }          US only
 *     { "regions": ["SG", "US"] }    both
 *     { "region": "US" }             singular form, also honoured (same as the US app)
 *
 * Rules for Singapore:
 * - `regions` contains "SG" (case-insensitive)      -> allowed
 * - no region field at all, or an empty list        -> allowed (every Singapore client predates
 *                                                       the flag; missing means Singapore-legacy)
 * - a well-formed list that lacks "SG" (US-only)    -> blocked; the SPA shows RegionNoAccessPage
 * - a malformed field (not a list / not a string)   -> allowed (fail open: a typo in metadata must
 *                                                       never lock a paying Singapore client out)
 * - the OPTRA admin email                           -> always allowed
 *
 * The US app implements the mirror image of this in OPTRA US/grant-eligibility/src/lib/auth/config.ts
 * (`metadataGrantsUs`). The backend enforces the same rule from `client_accounts.region`
 * (backend/app/services/region_access.py).
 */
import { isOptraAdminEmail } from "@/lib/adminAccess";

export const SG_REGION = "SG";
/** Region-relative route (under /sg) that shows RegionNoAccessPage to a signed-in US-only account. */
export const SG_NO_ACCESS_PATH = "/no-access";

function normalizeRegionCode(value) {
  if (typeof value !== "string") return null;
  const code = value.trim().toUpperCase();
  return code || null;
}

/**
 * Region codes declared in Clerk public metadata, upper-cased.
 * Returns `null` when no well-formed region field is present (absent, or `regions` is not an
 * array and `region` is not a string), so callers can tell "no flag" from "flag without SG".
 * An array of codes (possibly empty) otherwise.
 */
export function regionsFromPublicMetadata(md) {
  if (!md || typeof md !== "object" || Array.isArray(md)) return null;
  const listField = md.regions;
  const singleField = md.region;
  const listIsWellFormed = Array.isArray(listField);
  const singleIsWellFormed = typeof singleField === "string";
  if (!listIsWellFormed && !singleIsWellFormed) return null;

  const codes = new Set();
  if (listIsWellFormed) {
    for (const entry of listField) {
      const code = normalizeRegionCode(entry);
      if (code) codes.add(code);
    }
  }
  if (singleIsWellFormed) {
    const code = normalizeRegionCode(singleField);
    if (code) codes.add(code);
  }
  return [...codes];
}

/**
 * True when the signed-in Clerk user may use OPTRA Singapore. See the rules at the top of the file.
 * @param {{ publicMetadata?: unknown, email?: string | null }} args
 */
export function canAccessSg({ publicMetadata, email } = {}) {
  if (email && isOptraAdminEmail(email)) return true;
  const regions = regionsFromPublicMetadata(publicMetadata);
  if (regions === null || regions.length === 0) return true;
  return regions.includes(SG_REGION);
}

/** Primary email of a Clerk user resource (`useUser().user`), or "". */
export function clerkUserEmail(user) {
  return user?.primaryEmailAddress?.emailAddress || user?.emailAddresses?.[0]?.emailAddress || "";
}

/** True when a Clerk user resource may use OPTRA Singapore (canAccessSg over its public metadata and email). */
export function clerkUserCanAccessSg(user) {
  if (!user) return true;
  return canAccessSg({ publicMetadata: user.publicMetadata, email: clerkUserEmail(user) });
}

/**
 * Region-relative route a freshly signed-in Clerk user is sent to: the requested page, or
 * SG_NO_ACCESS_PATH for a US-only account.
 */
export function postSignInTarget(user, redirectTo) {
  if (!clerkUserCanAccessSg(user)) return SG_NO_ACCESS_PATH;
  return redirectTo || "/dashboard";
}
